import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // ✅ To detect current route

  // ✅ Scroll + Navigate
  const handleNavigate = (path) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsOpen(false);
    setMobileProductsOpen(false);
    setTimeout(() => navigate(path), 200);
  };

  // ✅ Helper for active link
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 flex justify-between items-center py-3">
        {/* Logo */}
        <div
          className="flex items-center space-x-2 cursor-pointer"
          onClick={() => handleNavigate("/")}
        >
          <img
            src="/img/logo.png"
            alt="Torshia Traders"
            className="h-12 w-auto object-contain"
          />
          <div className="hidden md:block">
            <span className="text-gray-900 text-xl font-bold">Torshia Traders</span>{" "}
            <span className="text-red-500 text-sm font-semibold">Pvt. Ltd</span>
          </div>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8 font-medium text-[16px]">
          <button
            onClick={() => handleNavigate("/")}
            className={`transition-all ${
              isActive("/")
                ? "text-red-600 font-semibold border-b-2 border-red-600"
                : "text-gray-700 hover:text-red-600"
            }`}
          >
            Home
          </button>

          <button
            onClick={() => handleNavigate("/about")}
            className={`transition-all ${
              isActive("/about")
                ? "text-red-600 font-semibold border-b-2 border-red-600"
                : "text-gray-700 hover:text-red-600"
            }`}
          >
            About
          </button>

          {/* All Products Dropdown */}
          <div className="relative group">
            <button
              className={`flex items-center transition-all ${
                location.pathname.startsWith("/products")
                  ? "text-red-600 font-semibold border-b-2 border-red-600"
                  : "text-gray-700 hover:text-red-600"
              }`}
            >
              All Products
              <ChevronDown className="w-4 h-4 ml-1" />
            </button>
            <div className="absolute top-full left-0 bg-white text-gray-800 shadow-md mt-2 rounded-md w-44 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <button
                onClick={() => handleNavigate("/products/chemicals")}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Chemicals
              </button>
              <button
                onClick={() => handleNavigate("/products/equipment")}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Equipment
              </button>
            </div>
          </div>

          <button
            onClick={() => handleNavigate("/faqs")}
            className={`transition-all ${
              isActive("/faqs")
                ? "text-red-600 font-semibold border-b-2 border-red-600"
                : "text-gray-700 hover:text-red-600"
            }`}
          >
            FAQs
          </button>

          <button
            onClick={() => handleNavigate("/contact")}
            className={`transition-all ${
              isActive("/contact")
                ? "text-red-600 font-semibold border-b-2 border-red-600"
                : "text-gray-700 hover:text-red-600"
            }`}
          >
            Contact
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-gray-800"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md text-gray-800 shadow-md border-t border-gray-200">
          <button
            onClick={() => handleNavigate("/")}
            className={`block w-full text-left px-4 py-3 border-b border-gray-200 ${
              isActive("/")
                ? "text-red-600 font-semibold bg-gray-100"
                : "hover:bg-gray-100"
            }`}
          >
            Home
          </button>

          <button
            onClick={() => handleNavigate("/about")}
            className={`block w-full text-left px-4 py-3 border-b border-gray-200 ${
              isActive("/about")
                ? "text-red-600 font-semibold bg-gray-100"
                : "hover:bg-gray-100"
            }`}
          >
            About
          </button>

          {/* Mobile All Products Dropdown */}
          <div className="border-b border-gray-200">
            <button
              onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
              className={`w-full flex justify-between px-4 py-3 text-left font-medium ${
                location.pathname.startsWith("/products")
                  ? "text-red-600 font-semibold bg-gray-100"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              All Products
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  mobileProductsOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileProductsOpen && (
              <div className="pl-6 pb-2">
                <button
                  onClick={() => handleNavigate("/products/chemicals")}
                  className="block w-full text-left py-2 hover:text-red-600"
                >
                  Chemicals
                </button>
                <button
                  onClick={() => handleNavigate("/products/equipment")}
                  className="block w-full text-left py-2 hover:text-red-600"
                >
                  Equipment
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => handleNavigate("/faqs")}
            className={`block w-full text-left px-4 py-3 border-b border-gray-200 ${
              isActive("/faqs")
                ? "text-red-600 font-semibold bg-gray-100"
                : "hover:bg-gray-100"
            }`}
          >
            FAQs
          </button>
          <button
            onClick={() => handleNavigate("/contact")}
            className={`block w-full text-left px-4 py-3 ${
              isActive("/contact")
                ? "text-red-600 font-semibold bg-gray-100"
                : "hover:bg-gray-100"
            }`}
          >
            Contact
          </button>
        </div>
      )}
    </nav>
  );
}
